import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import RMModal from 'components/modal/RMModal';
import { EReportTypes } from 'enums/EIconPanels';
import React, { useEffect, useRef, useState } from 'react';
import { ExperimentReportOptions } from './ExperimentReportOptions';
import { SingleDropReportOptions } from './SingleDropReportOptions';
import './experimentReports.css';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { fetchExperimentAsync } from 'components/searchAndFilter/utils';
import {
  useLatestSelectedSearchRow,
  useSelectedSearchTargetName,
} from 'redux/hooks/searchnFilter';
import { FmlxModal } from 'fmlx-common-ui';

type SearchAndFilterReportsDialogueProps = {
  reportType: string;
  setReportType: (e: string) => void;
  onPopupBlocked?: () => void;
};
export const SearchAndFilterReportsDialogue = ({
  reportType,
  setReportType,
  onPopupBlocked,
}: SearchAndFilterReportsDialogueProps) => {
  const reportOptionRef = useRef(null);
  const [docData, setDocData] = useState<ExperimentDtoClient>();
  const selectedSearchTargetType = useSelectedSearchTargetName();
  const latestSelectedSearchRow = useLatestSelectedSearchRow();
  const [showOptionsModal, setShowOptionsModal] = useState<boolean>(false);
  const [snfDropData, setSnfDropData] = useState<any>(null);
  const [okayDisabled, setOkayDisabled] = useState(false);

  useEffect(() => {
    if (!latestSelectedSearchRow) {
      return;
    }
    let treeNodeType;
    let selectedRowId;
    if (selectedSearchTargetType == ETreeNodeTypes.Experiment) {
      treeNodeType = ETreeNodeTypes.Experiment;
      selectedRowId = latestSelectedSearchRow.__ExperimentID;
    }
    if (selectedSearchTargetType == ETreeNodeTypes.Screen) {
      selectedRowId = latestSelectedSearchRow._ScreenID;
      treeNodeType = ETreeNodeTypes.Screen;
    }

    if (
      treeNodeType == ETreeNodeTypes.Experiment ||
      treeNodeType == ETreeNodeTypes.Screen
    ) {
      async function fetchExperimentData() {
        try {
          const data = await fetchExperimentAsync(selectedRowId, treeNodeType);
          setDocData(data);
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      }

      fetchExperimentData();
    }

    if (
      selectedSearchTargetType == 'Well Drop' ||
      selectedSearchTargetType == 'Drops by Score'
    ) {
      const dropData = {} as any;
      dropData.experimentId = latestSelectedSearchRow['Experiment.ID'];
      dropData.wellDropNumber = latestSelectedSearchRow['Well Drop.Number'];
      dropData.wellNumber = latestSelectedSearchRow['Well.Number'];
      dropData.wellDropId = latestSelectedSearchRow['Well Drop.ID'];
      dropData.plateId = latestSelectedSearchRow['Experiment Plate.ID'];
      dropData.captureResultProfileId =
        latestSelectedSearchRow['Capture Result.Profile ID'];
      dropData.imageBatchId = latestSelectedSearchRow['Capture Result.Image Batch ID'];
      setSnfDropData(dropData);

      async function fetchExperimentData() {
        try {
          const data = await fetchExperimentAsync(
            dropData.experimentId,
            ETreeNodeTypes.Experiment,
          );
          setDocData(data);
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      }

      fetchExperimentData();
    }
  }, [latestSelectedSearchRow]);

  useEffect(() => {
    if (docData && reportType != '') {
      setShowOptionsModal(true);
    }
  }, [docData]);

  function getDialogueClass() {
    if (docData?.isDirty) {
      return 'modal-fullw';
    } else if (reportType === EReportTypes.SingleDropReport) {
      return 'modal-singleDropReportWidth';
    } else {
      return 'modal-fullw';
    }
  }

  const getTitleColor = () => {
    if (reportType == EReportTypes.IngredientReport) {
      return '#1B1B1B';
    } else {
      return '#F7A246';
    }
  };

  function getModalBody() {
    let body;
    if (docData?.isDirty) {
      return (body = 'Please save experiment before generating report.');
    }
    switch (reportType) {
      case EReportTypes.ExperimentReport:
        body = (
          <ExperimentReportOptions
            docData={docData}
            ref={reportOptionRef}
            onFetchingData={setOkayDisabled}
          />
        );
        break;
      case EReportTypes.SingleDropReport:
        if (docData?.layers.length > 0) {
          body = (
            <SingleDropReportOptions
              docData={docData}
              ref={reportOptionRef}
              snfDropData={snfDropData}
            />
          );
        } else {
          setReportType('');
          showAlertSnackBar(
            EAlertType.WARNING,
            ESnackBarPosition.TOP,
            'This plate does not have any inspection.',
          );
        }
        break;
    }
    return body;
  }

  async function previewReport() {
    await generateReport(false);
  }

  async function saveReport() {
    await generateReport(true);
  }

  async function generateReport(saveReport: boolean) {
    const closeModal = true;
    switch (reportType) {
      case EReportTypes.ExperimentReport:
        await reportOptionRef.current.generateAction(saveReport, onPopupBlocked);
        break;
      case EReportTypes.SingleDropReport:
        await reportOptionRef.current.generateAction(saveReport, onPopupBlocked);
        break;
    }
    if (closeModal === true) {
      setReportType('');
    }
  }

  function getHeading() {
    let header;
    if (docData?.isDirty) {
      header = 'Warning';
      return header;
    }
    switch (reportType) {
      case EReportTypes.ExperimentReport:
        header = 'Experiment Report Options';
        break;
      case EReportTypes.SingleDropReport:
        header = 'Single Drop Report Options';
        break;
    }
    return header;
  }

  function getOkayButtonText() {
    const OkButtonText = 'Preview';
    return OkButtonText;
  }

  function getSaveButtonAvailable() {
    let saveAvailable = true;
    if (docData?.isDirty) {
      saveAvailable = false;
    }
    if (reportType === EReportTypes.ScoreReport) {
      saveAvailable = false;
    }
    return saveAvailable;
  }

  const getModalSize = () => {
    if (reportType === EReportTypes.SingleDropReport) {
      return FmlxModal.Size.EXTRA_LARGE;
    }
    return FmlxModal.Size.MEDIUM;
  };

  return (
    <>
      {showOptionsModal && (
        <RMModal
          id="seachandfiltereportsdialogue-modal-wrapper"
          showModal={reportType != ''}
          setShowModal={() => setReportType('')}
          body={getModalBody()}
          onOk={previewReport}
          onSave={saveReport}
          isCancelOnly={docData?.isDirty}
          okButtonText={getOkayButtonText()}
          saveButtonText="Save"
          noButtonText="Close"
          okayDisabled={okayDisabled}
          saveDisabled={okayDisabled}
          isSaveAvailable={getSaveButtonAvailable()}
          dialogClassName={getDialogueClass()}
          heading={getHeading()}
          showButtons={reportType !== EReportTypes.SynchrotronTripReport}
          titleClr={getTitleColor()}
          size={getModalSize()}
        />
      )}
    </>
  );
};
